import {ExternalLink, Pen, Trash2} from "lucide-react";
import {FC, useState} from "react";

interface Props {
  name: string;
  url: string;
  disabled?: boolean;
  editable?: boolean;
  onChange?: (name: string, url: string) => void;
  onDelete?: () => void;
}

function colorForName(name: string) {
  const known: Record<string, string> = {
    "ProAdvisor Reviews": "#57B98F",
    "Google Reviews": "#EA4335",
  };
  if (known[name]) return known[name];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0;
  const palette = ["#57B98F", "#3D52AD", "#F07340", "#52A882", "#8490AA"];
  return palette[Math.abs(hash) % palette.length];
}

const Review: FC<Props> = ({name, url, disabled, editable, onChange, onDelete}) => {
  const [editing, setEditing] = useState(false);
  const [draftUrl, setDraftUrl] = useState(url);
  const color = colorForName(name);

  const onSave = () => {
    if (!draftUrl.trim()) return;
    setEditing(false);
    onChange?.(name, draftUrl.trim());
  };

  return (
    <div
      className={
        [
          "flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs transition-colors bg-white",
          "border-brand-border",
          disabled && "pointer-events-none opacity-60",
        ].asClass
      }
    >
      <div className="w-2 h-2 rounded-full flex-shrink-0" style={{backgroundColor: color}} />

      {editing ? (
        <input
          type="text"
          value={draftUrl}
          autoFocus
          onChange={(e) => setDraftUrl(e.target.value)}
          placeholder="Review URL…"
          className="text-xs min-w-0 border-none bg-transparent focus:outline-none w-36 py-0 leading-none"
          style={{color: "#282D4D"}}
          onKeyDown={(e) => {
            if (e.key === "Enter") onSave();
            if (e.key === "Escape") {
              setEditing(false);
              setDraftUrl(url);
            }
          }}
        />
      ) : url ? (
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-1 text-brand-navy hover:text-brand-orange transition-colors"
        >
          <ExternalLink className="w-3 h-3" />
          {name}
        </a>
      ) : (
        <span className="text-brand-muted">{name}</span>
      )}

      {editable &&
        (editing ? (
          <div className="flex items-center gap-1 ml-auto flex-shrink-0">
            <button
              onClick={onSave}
              className="text-xs font-bold text-brand-navy hover:text-brand-orange transition-colors"
            >
              ✓
            </button>
            <button
              onClick={() => {
                setEditing(false);
                setDraftUrl(url);
              }}
              className="text-xs text-brand-muted hover:text-brand-navy transition-colors"
            >
              ✕
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-1 ml-auto flex-shrink-0">
            <button
              onClick={() => setEditing(true)}
              disabled={disabled}
              className="text-brand-muted hover:text-brand-navy transition-colors"
            >
              <Pen className="w-3 h-3" />
            </button>
            {onDelete && (
              <button
                onClick={onDelete}
                disabled={disabled}
                className="text-red-300 hover:text-red-500 transition-colors"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            )}
          </div>
        ))}
    </div>
  );
};

export default Review;
